import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, ArrowRight, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppLink, getContact } from "@/lib/site";
import { useContent } from "@/lib/content";

const COURSE_OPTIONS = ["ACCA", "CMA USA", "CA", "CS", "CFA", "Not sure yet"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { content } = useContent();
  const contact = getContact(content);
  const s = content.social || {};
  const socials = [
    { url: s.facebook, Icon: Facebook, label: "Facebook", testid: "contact-social-facebook", color: "#1877F2" },
    { url: s.instagram, Icon: Instagram, label: "Instagram", testid: "contact-social-instagram", color: "#E4405F" },
    { url: s.linkedin, Icon: Linkedin, label: "LinkedIn", testid: "contact-social-linkedin", color: "#0A66C2" },
    { url: s.youtube, Icon: Youtube, label: "YouTube", testid: "contact-social-youtube", color: "#FF0000" },
  ].filter((x) => x.url && x.url.trim());
  const [form, setForm] = useState({ name: "", phone: "", course: "ACCA", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.course) {
      toast.error("Please fill in your name, phone and course of interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "website-contact-form" });
      setDone(true);
      toast.success("Thanks! Opening WhatsApp with our counsellor…");
      const text = `Hi Educators Institute! I'm ${form.name}. I'm interested in ${form.course}. My phone: ${form.phone}.${form.message ? " Note: " + form.message : ""}`;
      window.open(buildWhatsAppLink(text, contact.whatsappRaw), "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      toast.error("Could not save your details. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Get In Touch</p>
          <h2 className="mt-3 font-rubik text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] leading-tight text-balance">
            One quick call. <span className="text-[#002570]">A clearer future.</span>
          </h2>
          <p className="mt-4 font-inter text-base md:text-lg text-slate-600 max-w-md">
            Drop your details and our counsellor will WhatsApp you within minutes — even on Sundays.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${contact.phoneRaw}`} data-testid="contact-phone-link" className="flex items-start gap-4 p-5 rounded-2xl bg-[#f8fafc] border border-slate-100 hover:border-[#73c3ff] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#002570] text-white flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Call / WhatsApp</p>
                <p className="font-rubik text-base font-semibold text-[#0f172a] mt-0.5">{contact.phone}</p>
              </div>
            </a>
            <a href={`mailto:${contact.email}`} data-testid="contact-email-link" className="flex items-start gap-4 p-5 rounded-2xl bg-[#f8fafc] border border-slate-100 hover:border-[#73c3ff] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#002570] text-white flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Email</p>
                <p className="font-rubik text-base font-semibold text-[#0f172a] mt-0.5 break-all">{contact.email}</p>
              </div>
            </a>
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#f8fafc] border border-slate-100">
              <div className="w-11 h-11 rounded-xl bg-[#002570] text-white flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Visit Us</p>
                <p className="font-inter text-sm text-slate-700 mt-0.5 leading-relaxed">{contact.address}</p>
              </div>
            </div>
          </div>

          {socials.length > 0 && (
            <div className="mt-6" data-testid="contact-social-bar">
              <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Follow us</p>
              <div className="flex items-center gap-3">
                {socials.map(({ url, Icon, label, testid, color }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={testid}
                    aria-label={label}
                    className="group w-12 h-12 rounded-xl bg-white border border-slate-200 hover:border-transparent flex items-center justify-center transition-all hover:shadow-lg hover:-translate-y-0.5"
                    style={{}}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = color; e.currentTarget.style.color = "white"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.color = ""; }}
                  >
                    <Icon className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="lead-capture-form"
            className="bg-[#f8fafc] rounded-[2rem] p-6 md:p-10 border border-slate-100 shadow-sm"
          >
            <h3 className="font-rubik text-2xl md:text-3xl font-bold text-[#0f172a]">Book Free Career Consultation</h3>
            <p className="mt-2 font-inter text-sm text-slate-600">No commitment. No spam. Just a clear next step.</p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="lead-name">Full Name *</label>
                <input
                  id="lead-name"
                  data-testid="lead-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  placeholder="e.g. Anjali Krishnan"
                  className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                />
              </div>
              <div>
                <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="lead-phone">Phone *</label>
                <input
                  id="lead-phone"
                  data-testid="lead-phone-input"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  required
                  type="tel"
                  inputMode="tel"
                  placeholder="+91 90000 00000"
                  className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="lead-course">Course of Interest *</label>
              <select
                id="lead-course"
                data-testid="lead-course-select"
                value={form.course}
                onChange={(e) => update("course", e.target.value)}
                className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
              >
                {COURSE_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="mt-5">
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="lead-msg">Anything we should know? (optional)</label>
              <textarea
                id="lead-msg"
                data-testid="lead-message-textarea"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={3}
                placeholder="Background, exam history, questions about fees..."
                className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading || done}
              data-testid="lead-submit-btn"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-4 font-inter font-medium transition-all hover:shadow-xl"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              ) : done ? (
                <><CheckCircle2 className="w-4 h-4" /> Sent — Check WhatsApp</>
              ) : (
                <>Submit & Open WhatsApp <ArrowRight className="w-4 h-4" /></>
              )}
            </button>

            <p className="mt-4 font-inter text-xs text-slate-500 text-center">
              By submitting you agree to be contacted by Educators Institute via WhatsApp/Phone.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
